import React, {useEffect, useRef, useState, useMemo, useCallback} from 'react';
import Main from "../../modules/main/main";
import usePermissions from "../../services/auth/usePermissions";
import {useNavigate} from "react-router-dom";
import { AgGridReact } from 'ag-grid-react';
import ReactLoading from 'react-loading';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {apiRoles} from "../../services/api/roles";
import DialogModule from "../../modules/dialog/dialog";
import {useForm} from "react-hook-form";

function Roles(props) {
    const navigate = useNavigate();
    const [userCan] = usePermissions();
    const breadcrumbs = [
        {"link": "/", "name": "Roles", "active": true}
    ];

    const gridRef = useRef();
    const [open, setOpen] = React.useState(false);
    const [error, setError] = useState('');
    const [paginationPageSize, setPaginationPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [allPermissions, setAllPermissions] = useState([]);
    const isFetchingRef = useRef(false);
    const createDataSourceRef = useRef(null);
    const [editingRole, setEditingRole] = useState(null);
    
    const handleOpen = () => {
        setEditingRole(null);
        reset();
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setEditingRole(null);
        reset();
    };

    const {register, handleSubmit, reset, watch, formState: {errors}} = useForm();
    const selectedPermissions = watch('permissions') || [];

    const [columnDefs] = useState([
        {
            field: 'name',
            headerName: 'Name'
        },
        {
            field: 'slug',
            headerName: 'Slug'
        },
        {
            field: 'description',
            headerName: 'Description'
        },
        {
            field: 'level',
            headerName: 'Level',
            type: 'numericColumn'
        },
        {
            headerName: 'Permissions',
            valueGetter: params => {
                if (!params.data?.permissions || !Array.isArray(params.data.permissions)) return '0';
                return params.data.permissions.length;
            },
            sortable: false,
            filter: false,
        },
        {
            headerName: 'Action',
            cellRenderer: params => {
                if(userCan('edit.roles') || userCan('view.roles')) {
                    return (
                        <span>
                            <button className={'btn btn-primary mb-2 mr-2'} onClick={() => handleEdit(params.data)}>Edit</button>
                            <button className={'btn btn-danger mb-2'} onClick={() => handleDelete(params.data.id)}>Delete</button>
                        </span>
                    );
                }
            },
            editable: false,
            sortable: false,
            filter: false,
            minWidth: 200,
        }
    ]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true,
        suppressMenu: true,
        cellStyle: { display: 'flex', alignItems: 'center' }
    }), []);

    const handleEdit = async (role) => {
        try {
            const res = await apiRoles.getSingle(role.id);
            setEditingRole(res.role);
            setAllPermissions(res.permissions || []);
            reset({
                name: res.role.name,
                slug: res.role.slug,
                description: res.role.description || '',
                level: res.role.level || 1,
                permissions: res.role.permissions ? res.role.permissions.map(p => p.id) : []
            });
            setOpen(true);
        } catch (err) {
            setError('Failed to load role');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this role?')) {
            try {
                await apiRoles.delete(id);
                if (gridRef.current && gridRef.current.api && createDataSourceRef.current) {
                    const dataSource = createDataSourceRef.current();
                    gridRef.current.api.setDatasource(dataSource);
                }
            } catch (err) {
                setError('Failed to delete role');
            }
        }
    };

    const onSave = async (data) => {
        setError('');
        try {
            // Convert permission IDs to array of integers
            if (data.permissions && !Array.isArray(data.permissions)) {
                data.permissions = [];
            }
            if (data.permissions) {
                data.permissions = data.permissions.map(id => parseInt(id));
            }
            
            let result;
            if (editingRole) {
                // Add _method for Laravel to recognize PATCH request
                data = {...data, '_method': 'PATCH'};
                result = await apiRoles.patch(editingRole.id, data);
            } else {
                result = await apiRoles.post(data);
            }
            
            // Check if result indicates success (has msg or role property, and no error)
            if(result && (result.msg || result.role) && !result.error && !result.status){
                // Success
                handleClose();
                if (gridRef.current && gridRef.current.api && createDataSourceRef.current) {
                    const dataSource = createDataSourceRef.current();
                    gridRef.current.api.setDatasource(dataSource);
                }
            } else {
                // Error - extract error message properly
                let errorMessage = 'An error occurred';
                if (result) {
                    if (result.error && typeof result.error === 'string') {
                        errorMessage = result.error;
                    } else if (result.message && typeof result.message === 'string') {
                        errorMessage = result.message;
                    } else if (result.data) {
                        if (typeof result.data === 'string') {
                            errorMessage = result.data;
                        } else if (typeof result.data === 'object') {
                            errorMessage = result.data.message || result.data.error || 'An error occurred';
                        }
                    } else if (typeof result === 'object' && result.message) {
                        errorMessage = result.message;
                    } else if (typeof result === 'string') {
                        errorMessage = result;
                    }
                }
                setError(errorMessage);
            }
        } catch (err) {
            // Extract error message from exception
            let errorMessage = 'Failed to save role';
            if (err) {
                if (err.response && err.response.data) {
                    if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data;
                    } else if (typeof err.response.data === 'object') {
                        errorMessage = err.response.data.message || err.response.data.error || 'Failed to save role';
                    }
                } else if (err.message && typeof err.message === 'string') {
                    errorMessage = err.message;
                } else if (typeof err === 'object' && err.message) {
                    errorMessage = err.message;
                } else if (typeof err === 'string') {
                    errorMessage = err;
                }
            }
            setError(errorMessage);
        }
    };

    const createDataSource = useCallback(() => {
        return {
            getRows: async (params) => {
                try {
                    isFetchingRef.current = true;
                    setLoading(true);
                    
                    const page = Math.floor(params.startRow / paginationPageSize) + 1;
                    const sortModel = params.sortModel;
                    
                    let sortParams = null;
                    if (sortModel && sortModel.length > 0) {
                        sortParams = {
                            colId: sortModel[0].colId,
                            sort: sortModel[0].sort
                        };
                    }
                    
                    const queryParams = new URLSearchParams();
                    queryParams.append('page', page);
                    queryParams.append('per_page', paginationPageSize);
                    
                    if (sortParams) {
                        queryParams.append('sort_by', sortParams.colId || 'created_at');
                        queryParams.append('sort_order', sortParams.sort || 'desc');
                    }
                    
                    const queryString = queryParams.toString();
                    const res = await apiRoles.getAll(queryString);
                    
                    let roles = [];
                    let totalRows = 0;
                    
                    if (res && res.roles) {
                        if (res.roles.data && Array.isArray(res.roles.data)) {
                            roles = res.roles.data;
                            totalRows = res.roles.total || 0;
                        } else if (Array.isArray(res.roles)) {
                            roles = res.roles;
                            totalRows = roles.length;
                        }
                    } else if (Array.isArray(res)) {
                        roles = res;
                        totalRows = res.length;
                    }
                    
                    // Store permissions for the form (only on first load)
                    if (res && res.permissions && allPermissions.length === 0) {
                        setAllPermissions(res.permissions);
                    }
                    
                    if (!Array.isArray(roles)) {
                        roles = [];
                    }
                    
                    const lastRow = totalRows > 0 ? totalRows - 1 : 0;
                    params.successCallback(roles, lastRow);
                    setLoading(false);
                    
                } catch (err) {
                    setError('Failed to load roles');
                    params.failCallback();
                    setLoading(false);
                } finally {
                    isFetchingRef.current = false;
                }
            }
        };
    }, [paginationPageSize, allPermissions.length]);
    
    createDataSourceRef.current = createDataSource;

    useEffect(() => {
        if(!userCan('view.roles')){
            navigate('/');
            return;
        }
    }, [userCan, navigate])

    // Load permissions when dialog opens
    useEffect(() => {
        if (open && allPermissions.length === 0) {
            apiRoles.getAll().then(res => {
                if (res && res.permissions) {
                    setAllPermissions(res.permissions);
                }
            });
        }
    }, [open, allPermissions.length]);

    return (
        <Main title="Roles" breadcrumbs={breadcrumbs} loading={false}>
            {error && <div className="alert alert-danger">
                <h5><i className="icon fas fa-ban"></i> Error!</h5>
                {error}
            </div>}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <label htmlFor="pageSizeSelect" className="mr-2">Page Size:</label>
                    <select
                        id="pageSizeSelect"
                        className="form-control form-control-sm d-inline-block"
                        style={{ width: 'auto' }}
                        value={paginationPageSize}
                        onChange={(e) => {
                            const newPageSize = Number(e.target.value);
                            setPaginationPageSize(newPageSize);
                            setLoading(true);
                            if (gridRef.current && gridRef.current.api) {
                                gridRef.current.api.paginationSetPageSize(newPageSize);
                                const dataSource = createDataSource();
                                gridRef.current.api.setDatasource(dataSource);
                                gridRef.current.api.paginationGoToFirstPage();
                            }
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
                {userCan('create.roles') &&
                    <button className={'btn btn-primary'} onClick={handleOpen}>Create Role</button>
                }
            </div>
            <div className="ag-theme-alpine" style={{ height: 800, width: '100%', position: 'relative' }}>
                <div className={loading ? "overlay" : "overlay d-none"}>
                    <ReactLoading type="spin" color="orange" width={'10%'} height={'10%'} />
                </div>
                <AgGridReact
                    style={{ width: '100%', height: '100%' }}
                    ref={gridRef}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowModelType="infinite"
                    pagination={true}
                    paginationPageSize={paginationPageSize}
                    cacheBlockSize={paginationPageSize}
                    maxBlocksInCache={10}
                    getRowId={(params) => params.data.id}
                    onGridReady={(params) => {
                        if (!params.api) {
                            return;
                        }
                        try {
                            params.api.sizeColumnsToFit();
                            const dataSource = createDataSource();
                            if (dataSource && dataSource.getRows) {
                                params.api.setDatasource(dataSource);
                            }
                        } catch (error) {
                            // Error setting datasource
                        }
                    }}
                    onSortChanged={(params) => {
                        if (params.api && !isFetchingRef.current) {
                            const dataSource = createDataSource();
                            params.api.setDatasource(dataSource);
                        }
                    }}
                    onFilterChanged={(params) => {
                        if (params.api && !isFetchingRef.current) {
                            params.api.purgeInfiniteCache();
                            params.api.paginationGoToFirstPage();
                            const dataSource = createDataSource();
                            params.api.setDatasource(dataSource);
                        }
                    }}
                    onColumnResized={(event) => event.api.sizeColumnsToFit()}
                    getRowHeight={() => 25}
                ></AgGridReact>
            </div>

            <DialogModule
                open={open}
                onClose={handleClose}
                setOpen={setOpen}
                title={editingRole ? "Edit Role" : "Create Role"}
                size="lg"
                fullWidth={true}
                hideButtons={true}
            >
                <form className="form-horizontal" onSubmit={handleSubmit(onSave)}>
                    {error && <div className="alert alert-danger">
                        <h5><i className="icon fas fa-ban"></i> Error!</h5>
                        {error}
                    </div>}
                    <div className="form-group row">
                        <label htmlFor="inputName" className="col-sm-2 col-form-label">Name</label>
                        <div className="col-sm-10">
                            <input
                                type="text"
                                {...register('name', { required: 'Name is required' })}
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Role Name"
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputSlug" className="col-sm-2 col-form-label">Slug</label>
                        <div className="col-sm-10">
                            <input
                                type="text"
                                {...register('slug')}
                                className="form-control"
                                placeholder="role-slug (auto-generated if empty)"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputDescription" className="col-sm-2 col-form-label">Description</label>
                        <div className="col-sm-10">
                            <textarea
                                {...register('description')}
                                className="form-control"
                                rows="3"
                                placeholder="Description"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputLevel" className="col-sm-2 col-form-label">Level</label>
                        <div className="col-sm-10">
                            <input
                                type="number"
                                {...register('level', { valueAsNumber: true, min: 1 })}
                                className="form-control"
                                placeholder="Level (default: 1)"
                                defaultValue={1}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label className="col-sm-2 col-form-label">Permissions</label>
                        <div className="col-sm-10">
                            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
                                {allPermissions.length === 0 ? (
                                    <p className="text-muted">Loading permissions...</p>
                                ) : (
                                    allPermissions.map(permission => (
                                        <div key={permission.id} className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                value={permission.id}
                                                id={`permission-${permission.id}`}
                                                {...register('permissions')}
                                                defaultChecked={editingRole && editingRole.permissions && editingRole.permissions.some(p => p.id === permission.id)}
                                            />
                                            <label className="form-check-label" htmlFor={`permission-${permission.id}`}>
                                                {permission.name} <small className="text-muted">({permission.slug})</small>
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="form-group row">
                        <div className="offset-sm-2 col-sm-10">
                            <button type="submit" className="btn btn-primary">
                                {editingRole ? 'Update' : 'Create'} Role
                            </button>
                            <button type="button" className="btn btn-secondary ml-2" onClick={handleClose}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </form>
            </DialogModule>
        </Main>
    );
}

export default Roles;

